import { PublicClientApplication, AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { msalInternalConfig, msalExternalConfig, loginRequest, AuthProvider } from "../config/msal-config";

// Use window to store state that survives HMR (Hot Module Replacement)
declare global {
  interface Window {
    __msalInternalInstance?: PublicClientApplication;
    __msalExternalInstance?: PublicClientApplication;
    __msalRedirectPromise?: Promise<AuthenticationResult | null>;
    __msalRedirectResult?: AuthenticationResult | null;
    __msalRedirectHandled?: boolean;
  }
}

// Get or create MSAL instance for internal tenant
export async function getInternalMsalInstance(): Promise<PublicClientApplication> {
  if (!window.__msalInternalInstance) {
    window.__msalInternalInstance = new PublicClientApplication(msalInternalConfig);
    await window.__msalInternalInstance.initialize();
  }
  return window.__msalInternalInstance;
}

// Get or create MSAL instance for external tenant
export async function getExternalMsalInstance(): Promise<PublicClientApplication> {
  if (!window.__msalExternalInstance) {
    window.__msalExternalInstance = new PublicClientApplication(msalExternalConfig);
    await window.__msalExternalInstance.initialize();
  }
  return window.__msalExternalInstance;
}

// Get the appropriate MSAL instance based on provider
export async function getMsalInstance(provider: AuthProvider): Promise<PublicClientApplication> {
  switch (provider) {
    case AuthProvider.EntraInternal:
      return getInternalMsalInstance();
    case AuthProvider.EntraExternal:
      return getExternalMsalInstance();
    default:
      throw new Error(`Unsupported auth provider: ${provider}`);
  }
}

// Initiate login redirect to Entra ID
export async function loginWithEntraId(provider: AuthProvider): Promise<void> {
  const msalInstance = await getMsalInstance(provider);

  // Store the provider in session storage so callback knows which instance to use
  sessionStorage.setItem("entraAuthProvider", provider);

  await msalInstance.loginRedirect({
    ...loginRequest,
    prompt: "select_account",
  });
}

// Start handling the redirect immediately when this module loads
// This must happen before React renders to catch the redirect response
function initializeRedirectHandling(): void {
  // Only initialize once (check window state to survive HMR)
  if (window.__msalRedirectPromise || window.__msalRedirectHandled) {
    return;
  }

  const provider = sessionStorage.getItem("entraAuthProvider") as AuthProvider;
  if (!provider) {
    return;
  }

  // Check if we're on the callback page
  const isOnCallbackPage = window.location.pathname === "/auth/callback";

  if (!isOnCallbackPage) {
    return;
  }

  // Start the redirect promise immediately
  window.__msalRedirectPromise = (async () => {
    try {
      const msalInstance = await getMsalInstance(provider);
      const response = await msalInstance.handleRedirectPromise();

      // Cache the result
      window.__msalRedirectResult = response;
      window.__msalRedirectHandled = true;

      return response;
    } catch (error) {
      window.__msalRedirectHandled = true;
      sessionStorage.removeItem("entraAuthProvider");
      return null;
    }
  })();
}

// Initialize immediately when module loads
initializeRedirectHandling();

// Handle the redirect callback after Entra ID authentication
export async function handleRedirectCallback(): Promise<AuthenticationResult | null> {
  const provider = sessionStorage.getItem("entraAuthProvider") as AuthProvider;
  if (!provider) {
    return null;
  }

  // If we already have a cached result, return it
  if (window.__msalRedirectHandled && window.__msalRedirectResult !== undefined) {
    return window.__msalRedirectResult;
  }

  // If we already started handling, await that promise
  if (window.__msalRedirectPromise) {
    return window.__msalRedirectPromise;
  }

  // Fallback: start handling now (shouldn't normally happen)
  const msalInstance = await getMsalInstance(provider);

  try {
    const response = await msalInstance.handleRedirectPromise();
    window.__msalRedirectResult = response;
    window.__msalRedirectHandled = true;
    return response;
  } catch (error) {
    window.__msalRedirectHandled = true;
    sessionStorage.removeItem("entraAuthProvider");
    return null;
  }
}

// Get the current account if user is already logged in
export async function getCurrentAccount(provider: AuthProvider): Promise<AccountInfo | null> {
  const msalInstance = await getMsalInstance(provider);
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

// Acquire token silently for API calls
export async function acquireTokenSilent(provider: AuthProvider): Promise<string | null> {
  const msalInstance = await getMsalInstance(provider);
  const account = await getCurrentAccount(provider);

  if (!account) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return response.accessToken;
  } catch (error) {
    return null;
  }
}

// Logout from Entra ID
export async function logoutFromEntraId(provider: AuthProvider): Promise<void> {
  const msalInstance = await getMsalInstance(provider);
  await msalInstance.logoutRedirect();
}
